import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import AuthContext from '../../context/authContext';
import useFrontendUsage from '../../hooks/useFrontendUsage';

const STATS_RIGHT = 'frontendUsage:statistics:read';

const LEGACY_COLOR = '#9e9e9e';
const NEW_COLOR = '#1976d2';
const REQUEST_SHARE_COLOR = '#9c27b0';

const defaultFrom = () => dayjs().subtract(29, 'day').format('YYYY-MM-DD');
const defaultTo = () => dayjs().format('YYYY-MM-DD');

const percent = (part, total) => {
  if (!total) return 0;
  return Math.round((part / total) * 1000) / 10;
};

const formatDateTime = value => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '—');

const displayName = row => {
  const full = [row.firstName, row.lastName].filter(Boolean).join(' ');
  return full || row.username || 'Unbekannt';
};

// Statistics view for the legacy vs. new management-frontend migration:
// who uses which frontend how much (users, activity, exact API-request share),
// where users switch between the tools, and why legacy users have not moved
// yet (recurring feedback prompt answers).
const FrontendMigrationStatistics = () => {
  const authCtx = useContext(AuthContext);
  const hasRight = authCtx.checkRight(STATS_RIGHT);

  const { getStatistics } = useFrontendUsage();

  const fromRef = useRef(null);
  const toRef = useRef(null);

  const [appliedFilter, setAppliedFilter] = useState(() => ({
    from: defaultFrom(),
    to: defaultTo(),
  }));
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!hasRight) return;
    let active = true;
    const loadStats = async () => {
      setIsLoading(true);
      try {
        const result = await getStatistics(appliedFilter);
        if (active) setStats(result);
      } catch (_error) {
        // Toast comes from the hook.
      } finally {
        if (active) setIsLoading(false);
      }
    };
    loadStats();
    return () => {
      active = false;
    };
  }, [hasRight, getStatistics, appliedFilter]);

  const handleApply = useCallback(() => {
    const from = fromRef.current?.value || defaultFrom();
    const to = toRef.current?.value || defaultTo();
    if (dayjs(from).isAfter(dayjs(to))) {
      toast.error('Das Startdatum darf nicht nach dem Enddatum liegen.');
      return;
    }
    setAppliedFilter({ from, to });
  }, []);

  const totalsByFrontend = useMemo(() => {
    const result = {
      legacy: { pageviews: 0, heartbeats: 0, userCount: 0 },
      new: { pageviews: 0, heartbeats: 0, userCount: 0 },
    };
    for (const row of stats?.totals || []) {
      if (result[row.frontend]) result[row.frontend] = row;
    }
    return result;
  }, [stats]);

  const activity = useMemo(() => {
    const legacy = totalsByFrontend.legacy.pageviews + totalsByFrontend.legacy.heartbeats;
    const nue = totalsByFrontend.new.pageviews + totalsByFrontend.new.heartbeats;
    return { legacy, new: nue, total: legacy + nue };
  }, [totalsByFrontend]);

  const requestTotals = useMemo(() => {
    let legacy = 0;
    let nue = 0;
    for (const row of stats?.apiRequests?.daily || []) {
      if (row.frontend === 'legacy') legacy += row.count;
      if (row.frontend === 'new') nue += row.count;
    }
    return { legacy, new: nue, total: legacy + nue };
  }, [stats]);

  const switchCounts = useMemo(() => {
    let toNew = 0;
    let toLegacy = 0;
    for (const row of stats?.switches?.totals || []) {
      if (row.targetFrontend === 'new') toNew += row.count;
      if (row.targetFrontend === 'legacy') toLegacy += row.count;
    }
    return { toNew, toLegacy };
  }, [stats]);

  const feedbackCounts = useMemo(() => {
    let submitted = 0;
    let dismissed = 0;
    for (const row of stats?.feedback?.totals || []) {
      if (row.eventType === 'feedback') submitted = row.count;
      if (row.eventType === 'feedback-dismissed') dismissed = row.count;
    }
    return { submitted, dismissed };
  }, [stats]);

  const reasonLabelByCode = useMemo(() => {
    const map = {};
    for (const reason of stats?.feedbackReasonLabels || []) {
      map[reason.code] = reason.label;
    }
    return map;
  }, [stats]);

  const newFrontendFeedback = stats?.newFrontendFeedback || null;

  const nfReasonLabelByCode = useMemo(() => {
    const map = {};
    for (const reason of newFrontendFeedback?.reasonLabels || []) {
      map[reason.code] = reason.label;
    }
    return map;
  }, [newFrontendFeedback]);

  const nfRatingCounts = useMemo(() => {
    const counts = { positive: 0, neutral: 0, negative: 0 };
    for (const row of newFrontendFeedback?.ratings || []) {
      if (row.rating in counts) counts[row.rating] = row.count;
    }
    return { ...counts, total: counts.positive + counts.neutral + counts.negative };
  }, [newFrontendFeedback]);

  // Daily activity + share series (merges event activity and API requests).
  const dailyChartData = useMemo(() => {
    const byDay = new Map();
    const ensure = day => {
      if (!byDay.has(day)) {
        byDay.set(day, {
          day,
          legacyActivity: 0,
          newActivity: 0,
          legacyUsers: 0,
          newUsers: 0,
          legacyRequests: 0,
          newRequests: 0,
        });
      }
      return byDay.get(day);
    };
    for (const row of stats?.daily || []) {
      const entry = ensure(row.day);
      if (row.frontend === 'legacy') {
        entry.legacyActivity = row.activity;
        entry.legacyUsers = row.userCount;
      } else if (row.frontend === 'new') {
        entry.newActivity = row.activity;
        entry.newUsers = row.userCount;
      }
    }
    for (const row of stats?.apiRequests?.daily || []) {
      const entry = ensure(row.day);
      if (row.frontend === 'legacy') entry.legacyRequests = row.count;
      if (row.frontend === 'new') entry.newRequests = row.count;
    }
    return [...byDay.values()]
      .sort((a, b) => (a.day < b.day ? -1 : 1))
      .map(entry => ({
        ...entry,
        label: dayjs(entry.day).format('DD.MM.'),
        newActivityShare: percent(entry.newActivity, entry.newActivity + entry.legacyActivity),
        newRequestShare: percent(entry.newRequests, entry.newRequests + entry.legacyRequests),
      }));
  }, [stats]);

  const switchPathsToNew = useMemo(
    () => (stats?.switches?.paths || []).filter(row => row.targetFrontend === 'new'),
    [stats]
  );
  const switchPathsToLegacy = useMemo(
    () => (stats?.switches?.paths || []).filter(row => row.targetFrontend === 'legacy'),
    [stats]
  );

  if (!hasRight) {
    return <Navigate to='/welcome' replace />;
  }

  const adoption = stats?.adoption || { onlyLegacy: 0, onlyNew: 0, both: 0 };
  const totalUsersInRange = adoption.onlyLegacy + adoption.both + adoption.onlyNew;
  const announcement = stats?.announcement || { activeUserCount: 0, announcementSeenCount: 0 };
  const heartbeatMinutes = stats?.heartbeatIntervalMinutes || 5;
  const activeHours = frontend =>
    Math.round((totalsByFrontend[frontend].heartbeats * heartbeatMinutes) / 6) / 10;

  const kpiCards = stats
    ? [
        {
          label: 'Nutzer neues Tool',
          value: totalsByFrontend.new.userCount,
          hint: `${percent(totalsByFrontend.new.userCount, totalUsersInRange)} % aller aktiven Nutzer`,
        },
        {
          label: 'Nutzer bisheriges Tool',
          value: totalsByFrontend.legacy.userCount,
          hint: `${percent(totalsByFrontend.legacy.userCount, totalUsersInRange)} % aller aktiven Nutzer`,
        },
        {
          label: 'Aktivitätsanteil neues Tool',
          value: `${percent(activity.new, activity.total)} %`,
          hint: `${activity.new} von ${activity.total} Events`,
        },
        {
          label: 'API-Request-Anteil neues Tool',
          value: `${percent(requestTotals.new, requestTotals.total)} %`,
          hint: `${requestTotals.new.toLocaleString('de-DE')} von ${requestTotals.total.toLocaleString('de-DE')} Requests`,
        },
        {
          label: 'Aktive Zeit (ca.)',
          value: `${activeHours('new')} h neu`,
          hint: `${activeHours('legacy')} h im bisherigen Tool`,
        },
        {
          label: 'Nur bisheriges / beide / nur neues',
          value: `${adoption.onlyLegacy} / ${adoption.both} / ${adoption.onlyNew}`,
          hint: `${percent(adoption.onlyLegacy, totalUsersInRange)} % / ${percent(adoption.both, totalUsersInRange)} % / ${percent(adoption.onlyNew, totalUsersInRange)} %`,
        },
        {
          label: 'Wechsel → neues Tool',
          value: switchCounts.toNew,
          hint: `${switchCounts.toLegacy} Wechsel zurück`,
        },
        {
          label: 'Ankündigung gesehen',
          value: `${announcement.announcementSeenCount} / ${announcement.activeUserCount}`,
          hint: 'aktive Nutzer',
        },
        {
          label: 'Feedback-Antworten',
          value: feedbackCounts.submitted,
          hint: `${feedbackCounts.dismissed}× „Später“`,
        },
      ]
    : [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant='h5' component='h1' sx={{ mb: 0.5 }}>
        Frontend-Migration
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Nutzung des bisherigen (management.fun-zone.de) und des neuen Management-Frontends
        (new.management.fun-zone.de) im Vergleich.
      </Typography>

      {/* Filter bar */}
      <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
          <TextField
            label='Von'
            type='date'
            inputRef={fromRef}
            defaultValue={appliedFilter.from}
            InputLabelProps={{ shrink: true }}
            size='small'
          />
          <TextField
            label='Bis'
            type='date'
            inputRef={toRef}
            defaultValue={appliedFilter.to}
            InputLabelProps={{ shrink: true }}
            size='small'
          />
          <Button variant='contained' onClick={handleApply}>
            Anwenden
          </Button>
        </Stack>
      </Paper>

      {isLoading && !stats ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* KPI cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {kpiCards.map(card => (
              <Grid item xs={6} sm={4} md={3} key={card.label}>
                <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
                  <Typography variant='caption' color='text.secondary'>
                    {card.label}
                  </Typography>
                  <Typography variant='h5'>{card.value}</Typography>
                  {card.hint && (
                    <Typography variant='caption' color='text.secondary'>
                      {card.hint}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* User distribution bar */}
          <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Nutzerverteilung im Zeitraum
            </Typography>
            {totalUsersInRange > 0 ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    height: 28,
                    borderRadius: 1,
                    overflow: 'hidden',
                    mb: 1,
                  }}
                >
                  {adoption.onlyLegacy > 0 && (
                    <Box
                      sx={{ width: `${(adoption.onlyLegacy / totalUsersInRange) * 100}%`, bgcolor: LEGACY_COLOR }}
                    />
                  )}
                  {adoption.both > 0 && (
                    <Box
                      sx={{ width: `${(adoption.both / totalUsersInRange) * 100}%`, bgcolor: '#26a69a' }}
                    />
                  )}
                  {adoption.onlyNew > 0 && (
                    <Box
                      sx={{ width: `${(adoption.onlyNew / totalUsersInRange) * 100}%`, bgcolor: NEW_COLOR }}
                    />
                  )}
                </Box>
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  <Chip
                    size='small'
                    variant='outlined'
                    sx={{ borderColor: LEGACY_COLOR }}
                    label={`Nur bisheriges Tool: ${adoption.onlyLegacy} (${percent(adoption.onlyLegacy, totalUsersInRange)} %)`}
                  />
                  <Chip
                    size='small'
                    variant='outlined'
                    sx={{ borderColor: '#26a69a' }}
                    label={`Beide Tools: ${adoption.both} (${percent(adoption.both, totalUsersInRange)} %)`}
                  />
                  <Chip
                    size='small'
                    variant='outlined'
                    sx={{ borderColor: NEW_COLOR }}
                    label={`Nur neues Tool: ${adoption.onlyNew} (${percent(adoption.onlyNew, totalUsersInRange)} %)`}
                  />
                </Stack>
              </>
            ) : (
              <Typography variant='body2' color='text.secondary'>
                Noch keine Nutzungsdaten im gewählten Zeitraum.
              </Typography>
            )}
          </Paper>

          {/* Daily activity */}
          <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Aktivität pro Tag (Seitenaufrufe + Heartbeats)
            </Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='label' />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey='legacyActivity'
                    name='Bisheriges Tool'
                    stackId='activity'
                    fill={LEGACY_COLOR}
                  />
                  <Bar dataKey='newActivity' name='Neues Tool' stackId='activity' fill={NEW_COLOR} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Share of the new frontend */}
          <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Anteil neues Tool pro Tag (%)
            </Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='label' />
                  <YAxis domain={[0, 100]} unit=' %' />
                  <Tooltip />
                  <Legend />
                  <Line
                    type='monotone'
                    dataKey='newActivityShare'
                    name='Aktivität'
                    stroke={NEW_COLOR}
                    dot={false}
                  />
                  <Line
                    type='monotone'
                    dataKey='newRequestShare'
                    name='API-Requests'
                    stroke={REQUEST_SHARE_COLOR}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Daily users */}
          <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Aktive Nutzer pro Tag
            </Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='label' />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type='monotone'
                    dataKey='legacyUsers'
                    name='Bisheriges Tool'
                    stroke={LEGACY_COLOR}
                    dot={false}
                  />
                  <Line
                    type='monotone'
                    dataKey='newUsers'
                    name='Neues Tool'
                    stroke={NEW_COLOR}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Switch behaviour */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
                <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
                  Von welchen Seiten wird ins neue Tool gewechselt?
                </Typography>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Seite</TableCell>
                        <TableCell align='right'>Wechsel</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {switchPathsToNew.map(row => (
                        <TableRow key={`${row.path}-new`}>
                          <TableCell>{row.viewTitle || row.path || '—'}</TableCell>
                          <TableCell align='right'>{row.count}</TableCell>
                        </TableRow>
                      ))}
                      {switchPathsToNew.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} align='center'>
                            Keine Wechsel im gewählten Zeitraum.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
                <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
                  Wechsel zurück ins bisherige Tool & Auslöser
                </Typography>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Seite (im neuen Tool)</TableCell>
                        <TableCell align='right'>Wechsel</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {switchPathsToLegacy.map(row => (
                        <TableRow key={`${row.path}-legacy`}>
                          <TableCell>{row.viewTitle || row.path || '—'}</TableCell>
                          <TableCell align='right'>{row.count}</TableCell>
                        </TableRow>
                      ))}
                      {switchPathsToLegacy.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} align='center'>
                            Keine Wechsel im gewählten Zeitraum.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant='subtitle2' sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                  Auslöser der Wechsel
                </Typography>
                <TableContainer>
                  <Table size='small'>
                    <TableBody>
                      {(stats?.switches?.totals || []).map(row => (
                        <TableRow key={`${row.targetFrontend}-${row.origin}`}>
                          <TableCell>
                            {row.origin === 'header-button'
                              ? 'Header-Button'
                              : row.origin === 'announcement-dialog'
                                ? 'Ankündigungs-Popup'
                                : row.origin === 'feedback-dialog'
                                  ? 'Feedback-Dialog'
                                  : row.origin || '—'}
                            {' → '}
                            {row.targetFrontend === 'new' ? 'neues Tool' : 'bisheriges Tool'}
                          </TableCell>
                          <TableCell align='right'>{row.count}</TableCell>
                        </TableRow>
                      ))}
                      {(stats?.switches?.totals || []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} align='center'>
                            Keine Daten.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>

          {/* Feedback */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={5}>
              <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
                <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
                  Warum wird das bisherige Tool noch bevorzugt?
                </Typography>
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Grund</TableCell>
                        <TableCell align='right'>Nennungen</TableCell>
                        <TableCell align='right'>Anteil</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(stats?.feedback?.reasonCounts || []).map(row => (
                        <TableRow key={row.code}>
                          <TableCell>{reasonLabelByCode[row.code] || row.code}</TableCell>
                          <TableCell align='right'>{row.count}</TableCell>
                          <TableCell align='right'>
                            {percent(row.count, feedbackCounts.submitted)} %
                          </TableCell>
                        </TableRow>
                      ))}
                      {(stats?.feedback?.reasonCounts || []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} align='center'>
                            Noch kein Feedback im gewählten Zeitraum.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={7}>
              <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
                <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
                  Letzte Feedback-Antworten
                </Typography>
                <Stack spacing={1.5}>
                  {(stats?.feedback?.recent || []).map((entry, index) => (
                    <Paper key={`${entry.occurredAt}-${index}`} variant='outlined' sx={{ p: 1.5 }}>
                      <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        flexWrap='wrap'
                        useFlexGap
                      >
                        <Typography variant='subtitle2'>{displayName(entry)}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {formatDateTime(entry.occurredAt)}
                          {entry.viewTitle ? ` · ${entry.viewTitle}` : ''}
                        </Typography>
                      </Stack>
                      {entry.reasons.length > 0 && (
                        <Stack direction='row' spacing={0.5} sx={{ mt: 0.5 }} flexWrap='wrap' useFlexGap>
                          {entry.reasons.map(code => (
                            <Chip
                              key={code}
                              size='small'
                              label={reasonLabelByCode[code] || code}
                            />
                          ))}
                        </Stack>
                      )}
                      {entry.comment && (
                        <Typography variant='body2' sx={{ mt: 0.5 }}>
                          „{entry.comment}“
                        </Typography>
                      )}
                    </Paper>
                  ))}
                  {(stats?.feedback?.recent || []).length === 0 && (
                    <Typography variant='body2' color='text.secondary'>
                      Noch keine Antworten im gewählten Zeitraum.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Dedicated feedback about the new frontend */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={5}>
              <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
                <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
                  Feedback zum neuen Tool (aus dem neuen Frontend)
                </Typography>
                <Stack direction='row' spacing={1} sx={{ mb: 2 }} flexWrap='wrap' useFlexGap>
                  <Chip
                    size='small'
                    color='success'
                    variant='outlined'
                    label={`${nfRatingCounts.positive}× positiv`}
                  />
                  <Chip size='small' variant='outlined' label={`${nfRatingCounts.neutral}× neutral`} />
                  <Chip
                    size='small'
                    color='error'
                    variant='outlined'
                    label={`${nfRatingCounts.negative}× negativ`}
                  />
                  <Chip
                    size='small'
                    label={`${percent(nfRatingCounts.positive, nfRatingCounts.total)} % positiv`}
                  />
                </Stack>
                <Typography variant='subtitle2' sx={{ mb: 1, fontWeight: 600 }}>
                  Kategorien
                </Typography>
                <TableContainer>
                  <Table size='small'>
                    <TableBody>
                      {(newFrontendFeedback?.reasonCounts || []).map(row => (
                        <TableRow key={row.code}>
                          <TableCell>{nfReasonLabelByCode[row.code] || row.code}</TableCell>
                          <TableCell align='right'>{row.count}</TableCell>
                        </TableRow>
                      ))}
                      {(newFrontendFeedback?.reasonCounts || []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} align='center'>
                            Noch keine Kategorien im gewählten Zeitraum.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography variant='subtitle2' sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
                  Seiten mit negativem Feedback
                </Typography>
                <TableContainer>
                  <Table size='small'>
                    <TableBody>
                      {(newFrontendFeedback?.negativePages || []).map(row => (
                        <TableRow key={row.path || 'unbekannt'}>
                          <TableCell>{row.viewTitle || row.path || '—'}</TableCell>
                          <TableCell align='right'>{row.count}</TableCell>
                        </TableRow>
                      ))}
                      {(newFrontendFeedback?.negativePages || []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={2} align='center'>
                            Kein negatives Feedback im gewählten Zeitraum.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={7}>
              <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
                <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
                  Letzte Rückmeldungen zum neuen Tool
                </Typography>
                <Stack spacing={1.5}>
                  {(newFrontendFeedback?.recent || []).map((entry, index) => (
                    <Paper
                      key={`${entry.occurredAt}-nf-${index}`}
                      variant='outlined'
                      sx={{ p: 1.5 }}
                    >
                      <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        flexWrap='wrap'
                        useFlexGap
                      >
                        <Chip
                          size='small'
                          color={
                            entry.rating === 'positive'
                              ? 'success'
                              : entry.rating === 'negative'
                                ? 'error'
                                : 'default'
                          }
                          label={
                            entry.rating === 'positive'
                              ? 'Positiv'
                              : entry.rating === 'negative'
                                ? 'Negativ'
                                : 'Neutral'
                          }
                        />
                        <Typography variant='subtitle2'>{displayName(entry)}</Typography>
                        <Typography variant='caption' color='text.secondary'>
                          {formatDateTime(entry.occurredAt)}
                          {entry.viewTitle ? ` · ${entry.viewTitle}` : ''}
                        </Typography>
                      </Stack>
                      {entry.reasons.length > 0 && (
                        <Stack
                          direction='row'
                          spacing={0.5}
                          sx={{ mt: 0.5 }}
                          flexWrap='wrap'
                          useFlexGap
                        >
                          {entry.reasons.map(code => (
                            <Chip
                              key={code}
                              size='small'
                              label={nfReasonLabelByCode[code] || code}
                            />
                          ))}
                        </Stack>
                      )}
                      {entry.comment && (
                        <Typography variant='body2' sx={{ mt: 0.5 }}>
                          „{entry.comment}“
                        </Typography>
                      )}
                    </Paper>
                  ))}
                  {(newFrontendFeedback?.recent || []).length === 0 && (
                    <Typography variant='body2' color='text.secondary'>
                      Noch keine Rückmeldungen im gewählten Zeitraum. Der Feedback-Button
                      (Sprechblase) befindet sich im Header des neuen Frontends.
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Per-user breakdown */}
          <Paper variant='outlined' sx={{ p: 2 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Nutzer im Detail (sortiert nach Aktivität im bisherigen Tool)
            </Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Nutzer</TableCell>
                    <TableCell align='right'>Aktivität bisher</TableCell>
                    <TableCell align='right'>Aktivität neu</TableCell>
                    <TableCell align='right'>Anteil neu</TableCell>
                    <TableCell align='right'>Wechsel → neu</TableCell>
                    <TableCell align='right'>Feedback</TableCell>
                    <TableCell>Zuletzt im neuen Tool</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats?.perUser || []).map(row => (
                    <TableRow key={row.userId}>
                      <TableCell>{displayName(row)}</TableCell>
                      <TableCell align='right'>{row.legacyActivity}</TableCell>
                      <TableCell align='right'>{row.newActivity}</TableCell>
                      <TableCell align='right'>
                        {percent(row.newActivity, row.newActivity + row.legacyActivity)} %
                      </TableCell>
                      <TableCell align='right'>{row.switchesToNew}</TableCell>
                      <TableCell align='right'>{row.feedbackCount}</TableCell>
                      <TableCell>{formatDateTime(row.lastNewAt)}</TableCell>
                    </TableRow>
                  ))}
                  {(stats?.perUser || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align='center'>
                        Keine Daten im gewählten Zeitraum.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default FrontendMigrationStatistics;
